import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  FlatList,
  TextInput,
  Dimensions,
  Modal,
  Platform,
  StatusBar,
  AccessibilityInfo,
  InteractionManager,
  findNodeHandle,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {createDropdownStyles} from '../../../../shared/components/ui/Dropdown/Dropdown.styles';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {
  DropdownOption,
  DropdownProps,
} from '../../../../shared/components/ui/Dropdown/DropdownProps';
import DropdownArrowIcon from '../../../../shared/assets/icons/DropdownArrowIcon';
import DropdownTickMark from '../../../../shared/assets/icons/DropdownTickMark';
import SearchIcon from '../../../../shared/assets/icons/SearchIcon';
import SearchArrowIcon from '../../../../shared/assets/icons/SearchArrowIcon';
import {accessibiltyText} from '../../../../shared/constants/global';
import {useAccessibilityProps} from '../../../../shared/hooks/useAccessibilityProps';
import Typography from '../../../constants/Typography';
import LargeCloseIcon from '@shared/assets/icons/LargeCloseIcon';

// ─── Module-level helpers (no nesting bonus from Dropdown scope) ─────────────

/** Focuses the first result or empty message after a search submit. CC ≈ 5 */
function focusFirstResultNode(firstResultNode: any, emptyMessageNode: any): void {
  const target = firstResultNode ?? emptyMessageNode;
  if (Platform.OS === 'web') {
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  } else {
    const handle = findNodeHandle(target);
    if (handle != null) {
      AccessibilityInfo.setAccessibilityFocus(handle);
    }
  }
}

/**
 * Resolves the DOM node that should receive programmatic focus when the
 * dropdown closes on web.  Extracted from restoreFocusToTriggerNode to
 * remove the nested ternary chain (which was the primary source of CC 17).
 */
function resolveDomFocusTarget(triggerNode: any): any {
  if (typeof triggerNode.focus === 'function') {return triggerNode;}
  if (triggerNode._nativeTag) {return document.querySelector('[data-focusable="true"]');}
  return null;
}

/** Returns accessibility focus to the dropdown trigger after close. CC ≈ 5 */
function restoreFocusToTriggerNode(triggerNode: any): void {
  if (Platform.OS === 'web') {
    if (triggerNode) {
      const domNode = resolveDomFocusTarget(triggerNode);
      if (domNode && typeof domNode.focus === 'function') {
        domNode.focus();
      }
    }
  } else {
    const handle = findNodeHandle(triggerNode);
    if (handle != null) {
      AccessibilityInfo.setAccessibilityFocus(handle);
    }
  }
}

/** Computes vertical position (top/bottom/panelMaxHeight) for the panel. CC ≈ 7 */
function computeVerticalDropdownPos(
  availableBelow: number,
  availableAbove: number,
  dropdownHeight: number,
  y: number,
  height: number,
  usableHeight: number,
  GAP: number,
): {top: number | undefined; bottom: number | undefined; panelMaxHeight: number} {
  if (availableBelow >= dropdownHeight) {
    return {top: y + height + GAP, bottom: undefined, panelMaxHeight: availableBelow};
  }
  if (availableAbove >= dropdownHeight) {
    const bottom =
      Platform?.OS === 'android' ? usableHeight - y + GAP - 58 : usableHeight - y + GAP;
    return {top: undefined, bottom, panelMaxHeight: availableAbove};
  }
  if (availableBelow >= availableAbove) {
    return {top: y + height + GAP, bottom: undefined, panelMaxHeight: availableBelow};
  }
  return {top: undefined, bottom: usableHeight - y + GAP, panelMaxHeight: availableAbove};
}

/** Computes horizontal position (left/right) for the panel. CC ≈ 6 */
function computeHorizontalDropdownPos(
  x: number,
  width: number,
  usableWidth: number,
): {left: number | undefined; right: number | undefined} {
  if (Platform.OS === 'web') {
    const panelRight = x + width;
    if (panelRight > usableWidth / 2 && x > usableWidth / 2) {
      return {left: undefined, right: Math.max(0, usableWidth - panelRight)};
    }
    return {left: Math.max(0, Math.min(x, usableWidth - width - 8)), right: undefined};
  }
  if (x > usableWidth / 2) {
    return {left: undefined, right: usableWidth - (x + width)};
  }
  return {left: x, right: undefined};
}

/** Computes maxWidth so the panel never overflows the viewport on web. CC ≈ 3 */
function computePanelMaxWidth(
  left: number | undefined,
  right: number | undefined,
  usableWidth: number,
): number | undefined {
  if (Platform.OS !== 'web') return undefined;
  if (left !== undefined) return usableWidth - left - 8;
  if (right !== undefined) return usableWidth - right - 8;
  return usableWidth - 16;
}

/** Handles search text changes with optional deferred filter. CC ≈ 4 */
function applySearchChange(
  text: string,
  showSearchArrow: boolean,
  setSearchText: (v: string) => void,
  applyFilter: (v: string) => void,
): void {
  setSearchText(text);
  if (showSearchArrow) {
    if (text === '') applyFilter('');
  } else {
    applyFilter(text);
  }
}

/**
 * Filters the options array by a search string, checking label and optionally
 * description.  Extracted from the applyFilter useCallback to remove nested
 * arrow CC and nesting-bonus penalties from the Dropdown component.
 */
function filterDropdownOptions(
  options: DropdownOption[],
  text: string,
  filterByLabelOnly: boolean,
): DropdownOption[] {
  const lower = text.toLowerCase();
  return options.filter(opt => {
    if ((opt.label ?? '').toLowerCase().includes(lower)) {return true;}
    if (!filterByLabelOnly && opt.desc) {return opt.desc.toLowerCase().includes(lower);}
    return false;
  });
}

/**
 * Updates the first/last visible FlatList item indices from the viewable-items
 * array.  Extracted from handleViewableItemsChanged to remove its nested
 * arrows and if/&& chains from the Dropdown component CC.
 */
function updateVisibleIndices(
  viewableItems: Array<{index: number | null; isViewable: boolean}>,
  setFirstVisibleIndex: (i: number) => void,
  setLastVisibleIndex: (i: number) => void,
): void {
  const visible = viewableItems.filter(vi => vi.isViewable);
  const first = visible[0];
  const last = visible[visible.length - 1];
  if (first?.index != null) {
    setFirstVisibleIndex(first.index);
  }
  if (last?.index != null) {
    setLastVisibleIndex(last.index);
  }
}

/**
 * Computes the isLast / isFirst flags used to apply rounded corners to the
 * first/last visible FlatList item.  Extracted from the renderItem useCallback
 * to remove its two ternaries and two && expressions from the Dropdown CC.
 */
function computeItemFlags(
  filteredLength: number,
  lastVisibleIndex: number,
  firstVisibleIndex: number,
  showsBelow: boolean,
  index: number,
): {isLast: boolean; isFirst: boolean} {
  const isScrollable = filteredLength > MAX_ITEMS_VISIBLE;
  const trueLastIndex = isScrollable ? lastVisibleIndex : filteredLength - 1;
  const trueFirstIndex = isScrollable ? firstVisibleIndex : 0;
  return {
    isLast: showsBelow && index === trueLastIndex,
    isFirst: !showsBelow && index === trueFirstIndex,
  };
}

/** Computes the capped list maxHeight for the FlatList style. CC ≈ 2 */
function computeListMaxHeight(
  showSearch: boolean,
  optionsLength: number,
  dropdownListMaxHeight: number,
  panelMaxHeight: number | undefined,
): number {
  const searchExtra = showSearch && optionsLength > 0 ? SEARCH_ROW_HEIGHT : 0;
  const available = Math.max(
    ITEM_HEIGHT + DROPDOWN_LIST_PADDING,
    (panelMaxHeight ?? dropdownListMaxHeight) - searchExtra,
  );
  return Math.min(dropdownListMaxHeight, available);
}

/**
 * Handles Escape key presses when the dropdown is open.
 * Extracted from the useEffect's nested handleKeyDown arrow function to remove
 * its nesting-level-2 CC penalty (if + && at depth 2 = +4) from Dropdown.
 */
function handleDropdownEscapeKey(
  e: KeyboardEvent,
  closeDropdownFn: (restoreFocus?: boolean) => void,
): void {
  if (e.key === 'Escape') {
    closeDropdownFn(true);
  }
}

/**
 * Opens the dropdown — called by handleTriggerPress when the panel is closed.
 * Separated from the close path so neither function has a boolean parameter
 * (satisfies SonarQube S2301 "provide multiple methods").
 */
function pressToOpenDropdown(
  measureDropdownFn: (cb?: () => void) => void,
  setIsOpen: (v: boolean) => void,
): void {
  if (Platform.OS === 'web') {
    measureDropdownFn();
    setIsOpen(true);
  } else {
    measureDropdownFn(() => setIsOpen(true));
  }
}

/**
 * Closes the dropdown and resets search / position state.
 * Extracted from the closeDropdown useCallback to remove its two `if` branches
 * (nesting level 1, CC +4) from the Dropdown component body.
 */
function performCloseDropdown(
  options: DropdownOption[],
  setIsOpen: (v: boolean) => void,
  setSearchText: (v: string) => void,
  setFilteredOptions: (opts: DropdownOption[]) => void,
  setIsPositioned: (v: boolean) => void,
  restoreFocusToTrigger: () => void,
  restoreFocus: boolean,
): void {
  setIsOpen(false);
  setSearchText('');
  setFilteredOptions(options);
  if (Platform.OS === 'web') {
    setIsPositioned(false);
  }
  if (restoreFocus) {
    restoreFocusToTrigger();
  }
}

/**
 * Computes a uniform minWidth from the average character count of all option labels.
 * Extracted from the averageLabelWidth useMemo to remove its `if` (level 1, +2) and
 * the `??` inside the nested reduce arrow (level 2, +3) from the Dropdown component.
 */
function computeAverageLabelWidth(options: DropdownOption[]): number {
  if (options.length === 0) {
    return 0;
  }
  const totalLength = options.reduce(
    (sum, o) => sum + (o.label ?? '').length,
    0,
  );
  // 8 px per average character + 32 px for horizontal padding / icon room.
  return Math.round((totalLength / options.length) * 8 + 32);
}

/** Callbacks passed to processMeasurementCallback to commit computed position. */
interface MeasurementHandlers {
  setDropdownPos: (pos: {
    top: number | undefined;
    bottom: number | undefined;
    left: number | undefined;
    right: number | undefined;
    width: number;
    maxWidth?: number;
    panelMaxHeight?: number;
  }) => void;
  setIsPositioned: (v: boolean) => void;
  onReady?: () => void;
}

/**
 * Resolves the node to measure and calls processMeasurementCallback.
 * Extracted from the measureDropdown useCallback to remove its `??` chain
 * (nesting level 1, +2) and `if (!nodeToMeasure)` (level 1, +2) from Dropdown.
 */
function executeMeasureDropdown(
  inputRef: React.RefObject<any> | undefined,
  triggerRef: React.MutableRefObject<any>,
  showSearch: boolean,
  optionsLength: number,
  handlers: MeasurementHandlers,
): void {
  const nodeToMeasure: {measureInWindow: Function} | null =
    inputRef?.current ?? (triggerRef.current as any) ?? null;
  if (!nodeToMeasure) {
    handlers.onReady?.();
    return;
  }
  nodeToMeasure.measureInWindow(
    (x: number, y: number, width: number, height: number) =>
      processMeasurementCallback(x, y, width, height, showSearch, optionsLength, handlers),
  );
}

/**
 * Syncs the internal triggerRef and the optional caller-supplied inputRef.
 * Extracted from the inline `ref={node => {...}}` to remove its `if (inputRef)`
 * branch (nesting level 1, CC +2) from the Dropdown component.
 */
function syncTriggerRef(
  node: any,
  triggerRef: React.MutableRefObject<any>,
  inputRef: React.RefObject<any> | undefined,
): void {
  triggerRef.current = node;
  if (inputRef) {
    (inputRef as React.MutableRefObject<any>).current = node;
  }
}

/**
 * Sets hover state only on web. Extracted from the inline onMouseEnter /
 * onMouseLeave arrow functions to remove their `&&` branches
 * (nesting level 1, CC +2 each) from the Dropdown component.
 */
function setDropdownHovered(
  isHovered: boolean,
  setIsHoveredFn: (v: boolean) => void,
): void {
  if (Platform.OS === 'web') {
    setIsHoveredFn(isHovered);
  }
}

/**
 * Registers the Escape-key listener when on web and returns the cleanup.
 * Extracted from the useEffect body to remove its `if (Platform.OS !== 'web')`
 * branch (nesting level 1, CC +2) from the Dropdown component.
 */
function setupEscapeKeyListener(
  isOpen: boolean,
  closeDropdownFn: (restoreFocus?: boolean) => void,
): (() => void) | void {
  if (Platform.OS !== 'web' || !isOpen) {
    return;
  }
  const handleKeyDown = (e: KeyboardEvent) =>
    handleDropdownEscapeKey(e, closeDropdownFn);
  document.addEventListener('keydown', handleKeyDown);
  return () => document.removeEventListener('keydown', handleKeyDown);
}

/**
 * Re-measures the dropdown position when the device orientation changes and
 * the panel is open.  Extracted from handleOrientationChange to remove its
 * `if (isOpen)` branch (nesting level 1, CC +2) from the Dropdown component.
 */
function triggerRemeasureIfOpen(
  isOpen: boolean,
  measureDropdownFn: () => void,
): void {
  if (isOpen) {
    measureDropdownFn();
  }
}

/**
 * Filters options and updates the filteredOptions state.
 * Extracted from the applyFilter useCallback to remove its if + || CC
 * (nesting level 1, CC +3) from the Dropdown component.
 */
function applyDropdownFilter(
  text: string,
  showSearch: boolean,
  options: DropdownOption[],
  filterByLabelOnly: boolean,
  setFilteredOptions: (opts: DropdownOption[]) => void,
): void {
  if (!showSearch || !text.trim()) {
    setFilteredOptions(options);
    return;
  }
  setFilteredOptions(filterDropdownOptions(options, text, filterByLabelOnly));
}

// ─── Per-item hover wrapper ──────────────────────────────────────────────────
interface DropdownMenuItemProps {
  item: DropdownOption;
  isSelected: boolean;
  /** True when this item is the last one visible in the dropdown viewport
   *  AND the panel opens below the trigger.
   *  Drives borderBottomLeftRadius / borderBottomRightRadius on Android where
   *  overflow:'hidden' is not available (it removes elevation shadow). */
  isLast: boolean;
  /** True when this item is the first one visible in the dropdown viewport
   *  AND the panel opens above the trigger.
   *  Drives borderTopLeftRadius / borderTopRightRadius on Android. */
  isFirst: boolean;
  styles: ReturnType<typeof createDropdownStyles>;
  theme: any;
  showTickMark: boolean;
  formatTextData: boolean;
  onSelect: (item: DropdownOption) => void;
  t: (key: string) => string;
  showSearch: boolean;
}

const DropdownMenuItem = memo(
  ({
    item,
    isSelected,
    isLast,
    isFirst,
    styles,
    theme,
    showTickMark,
    formatTextData,
    onSelect,
    t,
    showSearch
  }: DropdownMenuItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        style={[
          styles.menuItem,
          isSelected && styles.activeItem,
          isHovered && !isSelected && styles.menuItemHover,
          isLast && !showSearch && styles.menuItemLast,
          isFirst && !showSearch && styles.menuItemFirst,
        ]}
        onPress={() => {
          onSelect(item);
          AccessibilityInfo.announceForAccessibility(
            `${item.label} ${t(
              accessibiltyText.dropdown.itemSelectedAnnouncementKey,
            )}`,
          );
        }}
        accessible={true}
        accessibilityRole="menuitem"
        accessibilityLabel={
          item.accessibilityLabel ||
          (isSelected
            ? `${item.label}, ${t(accessibiltyText.dropdown.itemSelectedKey)}`
            : `${item.label}, ${t(
                accessibiltyText.dropdown.itemNotSelectedKey,
              )}`)
        }
        accessibilityState={{selected: isSelected}}
        // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
        onMouseEnter={() => setIsHovered(true)}
        // @ts-ignore
        onMouseLeave={() => setIsHovered(false)}
        // @ts-ignore — tabIndex / focusable are required for Tab-key focus on web
        tabIndex={0}
        focusable={true}>
        <View
          style={[
            styles.dropdownItemContainer,
            {flex: 1},
          ]}>
          <Text
            style={[styles.menuItemText, isSelected && styles.activeItemText]}>
            {item.label}
          </Text>
          {item.desc && (
            <Text
              style={[styles.descText, isSelected && styles.activeItemText]}>
              {item.desc}
            </Text>
          )}
        </View>
        {isSelected && showTickMark && (
          <View style={styles.tickMark}>
            <DropdownTickMark color={theme.selectedDropdownTextColor} />
          </View>
        )}
      </TouchableOpacity>
    );
  },
);

const ITEM_HEIGHT = Typography.height.height50;
const MAX_ITEMS_VISIBLE = 5;
const SEARCH_ROW_HEIGHT = 50;
/** Vertical padding applied inside the dropdownValues ScrollView.
 *  Top uses padding10 (tight fit at the top); bottom uses padding16 to give
 *  visible breathing room after the last item.  Both values must match the
 *  actual paddingTop / paddingBottom set on `styles.dropdownValues` below. */
const DROPDOWN_LIST_PADDING =
  Typography.padding.padding10 + Typography.padding.padding16;

/**
 * Schedules focusFirstResultNode via InteractionManager.
 * Extracted from the focusFirstResult useCallback to remove its nested arrow
 * (nesting level 2 inside Dropdown, CC +2) from the Dropdown component.
 */
function scheduleFocusFirstResult(
  firstResultRef: React.MutableRefObject<React.ElementRef<typeof TouchableOpacity> | null>,
  emptyMessageRef: React.MutableRefObject<React.ElementRef<typeof View> | null>,
): void {
  InteractionManager.runAfterInteractions(() =>
    focusFirstResultNode(firstResultRef.current, emptyMessageRef.current),
  );
}

/**
 * Schedules restoreFocusToTriggerNode via InteractionManager.
 * Extracted from the restoreFocusToTrigger useCallback to remove its nested
 * arrow (nesting level 2 inside Dropdown, CC +2) from the Dropdown component.
 */
function scheduleRestoreFocusTrigger(
  triggerRef: React.MutableRefObject<React.ElementRef<typeof TouchableOpacity> | null>,
): void {
  InteractionManager.runAfterInteractions(() =>
    restoreFocusToTriggerNode(triggerRef.current),
  );
}

/**
 * Computes the FlatList maxHeight based on the number of filtered options.
 * Extracted from the dropdownListMaxHeight useMemo to remove its arrow (CC +1)
 * from the Dropdown component.
 */
function computeDropdownListMaxHeight(filteredOptionsLength: number): number {
  const n = Math.min(filteredOptionsLength, MAX_ITEMS_VISIBLE);
  return (
    n * ITEM_HEIGHT +
    Math.max(0, n - 1) * Typography.gap.gap16 +
    DROPDOWN_LIST_PADDING
  );
}

/**
 * Computes the style array for the dropdown trigger TouchableOpacity.
 * Extracted to remove 4 × && and 2 × ternary operators (CC +6) from Dropdown.
 */
function computeTriggerButtonStyle(
  isOpen: boolean,
  showsBelow: boolean,
  isHovered: boolean,
  isDisabled: boolean,
  averageLabelWidth: number,
  styles: ReturnType<typeof createDropdownStyles>,
  customContainerStyles: any,
): Array<any> {
  return [
    styles.input,
    isOpen && (showsBelow ? styles.dropdownOpen : styles.dropdownOpenAbove),
    !isOpen && isHovered && styles.hoverInput,
    isOpen && styles.openInput,
    isDisabled && styles.disabledInput,
    averageLabelWidth > 0 ? {minWidth: averageLabelWidth} : undefined,
    customContainerStyles,
  ];
}

/**
 * Computes the style array for the dropdown panel View.
 * Extracted to remove 2 style ternaries + 4 spread ternaries (CC +6) from Dropdown.
 */
function computePanelViewStyle(
  showSearch: boolean,
  showsBelow: boolean,
  dropdownPos: {
    top: number | undefined;
    bottom: number | undefined;
    left: number | undefined;
    right: number | undefined;
    width: number;
    maxWidth?: number;
    panelMaxHeight?: number;
  },
  averageLabelWidth: number,
  isPositioned: boolean,
  styles: ReturnType<typeof createDropdownStyles>,
): Array<any> {
  return [
    showSearch ? styles.dropdownWithSearchMenu : styles.dropdownMenu,
    showsBelow ? styles.panelOpenBelow : styles.panelOpenAbove,
    {
      top: dropdownPos.top,
      bottom: dropdownPos.bottom,
      left: dropdownPos.left,
      right: dropdownPos.right,
      ...(averageLabelWidth > 0
        ? {width: Math.max(dropdownPos.width || 0, averageLabelWidth) || undefined}
        : {minWidth: dropdownPos.width || undefined}),
      ...(dropdownPos.maxWidth === undefined ? {} : {maxWidth: dropdownPos.maxWidth}),
      ...(dropdownPos.panelMaxHeight === undefined ? {} : {maxHeight: dropdownPos.panelMaxHeight}),
      opacity: isPositioned ? 1 : 0,
    },
  ];
}

/**
 * Processes measureInWindow results and commits the computed panel position.
 * Extracted from the measureDropdown useCallback to remove its Platform checks
 * and conditional branches from the Dropdown component CC.
 */
function processMeasurementCallback(
  x: number,
  y: number,
  width: number,
  height: number,
  showSearch: boolean,
  optionsLength: number,
  {setDropdownPos, setIsPositioned, onReady}: MeasurementHandlers,
): void {
  const usableWidth =
    Platform.OS === 'web'
      ? (window?.innerWidth ?? Dimensions.get('window').width)
      : Dimensions.get('window').width;
  const usableHeight =
    Platform.OS === 'web'
      ? (window?.innerHeight ?? Dimensions.get('window').height)
      : Dimensions.get('window').height;
  const statusBarHeight =
    Platform.OS === 'android' ? StatusBar.currentHeight || 0 : 0;
  const GAP = 6;
  const searchRowExtra = showSearch && optionsLength > 0 ? SEARCH_ROW_HEIGHT : 0;
  const n = Math.min(optionsLength, MAX_ITEMS_VISIBLE);
  const panelContentHeight =
    n * ITEM_HEIGHT +
    Math.max(0, n - 1) * Typography.gap.gap16 +
    DROPDOWN_LIST_PADDING;
  const dropdownHeight = panelContentHeight + searchRowExtra;
  const spaceBelow = usableHeight - (y + height) - statusBarHeight;
  const spaceAbove = y - statusBarHeight;
  const availableBelow = Math.max(0, spaceBelow - GAP);
  const availableAbove = Math.max(0, spaceAbove - GAP);
  const {top, bottom, panelMaxHeight} = computeVerticalDropdownPos(
    availableBelow, availableAbove, dropdownHeight, y, height, usableHeight, GAP,
  );
  const {left, right} = computeHorizontalDropdownPos(x, width, usableWidth);
  const maxWidth = computePanelMaxWidth(left, right, usableWidth);
  setDropdownPos({top, bottom, left, right, width, maxWidth, panelMaxHeight});
  if (Platform.OS === 'web') {
    setIsPositioned(true);
  } else {
    onReady?.();
  }
}

interface DropdownTriggerContentProps {
  showLabelWithSelected: boolean;
  label: string | undefined;
  selectedSort: DropdownOption | undefined;
  placeholder: string | undefined;
  numberOfLines: number | undefined;
  customLabelStyles: any;
  customTextStyles: any;
  customIcon: React.ReactNode | undefined;
  styles: ReturnType<typeof createDropdownStyles>;
  theme: any;
}

/**
 * Content rendered inside the Dropdown trigger TouchableOpacity.
 * Extracted to remove the showLabelWithSelected ternary, label/placeholder
 * || chains, and customIcon ternary from the Dropdown component CC.
 */
const DropdownTriggerContent: React.FC<DropdownTriggerContentProps> = ({
  showLabelWithSelected, label, selectedSort, placeholder, numberOfLines,
  customLabelStyles, customTextStyles, customIcon, styles, theme,
}) => (
  <>
    {showLabelWithSelected ? (
      <View style={[styles.showLabelWithSelected, numberOfLines != null && {flex: 1}]}>
        <Text
          style={[styles.showlabelText, customLabelStyles]}
          numberOfLines={numberOfLines}
          ellipsizeMode={numberOfLines == null ? undefined : 'tail'}>
          {label || selectedSort?.label || placeholder || ''}
        </Text>
        <Text
          style={[
            selectedSort?.label ? styles.inputText : styles.placeHodlerInputText,
            customTextStyles,
          ]}
          numberOfLines={numberOfLines}
          ellipsizeMode={numberOfLines == null ? undefined : 'tail'}>
          {selectedSort?.label || placeholder || ''}
        </Text>
      </View>
    ) : (
      <View style={[styles.showLabelWithSelected, numberOfLines != null && {flex: 1}]}>
        <Text
          style={[
            selectedSort?.label ? styles.inputText : styles.placeHodlerInputText,
            customTextStyles,
          ]}
          numberOfLines={numberOfLines}
          ellipsizeMode={numberOfLines == null ? undefined : 'tail'}>
          {label || selectedSort?.label || placeholder || ''}
        </Text>
      </View>
    )}
    {customIcon ?? <DropdownArrowIcon color={theme.dropdownIcon} />}
  </>
);

interface DropdownSearchRowProps {
  showSearch: boolean;
  optionsLength: number;
  searchText: string;
  showSearchArrow: boolean;
  searchPlaceholder: string | undefined;
  searchInputRef: React.RefObject<TextInput | null>;
  setSearchText: (v: string) => void;
  applyFilter: (v: string) => void;
  focusFirstResult: () => void;
  styles: ReturnType<typeof createDropdownStyles>;
  theme: any;
}

/**
 * Search row rendered at the top of the dropdown panel when showSearch is true.
 * Extracted to remove showSearch &&, searchText !== '' &&, and showSearchArrow &&
 * from the Dropdown component CC.
 */
const DropdownSearchRow: React.FC<DropdownSearchRowProps> = ({
  showSearch, optionsLength, searchText, showSearchArrow, searchPlaceholder,
  searchInputRef, setSearchText, applyFilter, focusFirstResult, styles, theme,
}) => {
  if (!showSearch || optionsLength === 0) return null;
  return (
    <View style={styles.searchRow}>
      <TouchableOpacity
        style={styles.searchIconWrapper}
        onPress={() => searchInputRef.current?.focus()}
        accessibilityRole="button"
        accessibilityLabel={searchPlaceholder ?? 'Search'}>
        <SearchIcon
          color={theme.dropdownTextColor}
          borderColor={theme.searchBorder}
        />
      </TouchableOpacity>
      <TextInput
        ref={searchInputRef}
        style={[
          styles.searchTextInput,
          Platform.OS === 'web' && ({outline: 'none'} as any),
        ]}
        value={searchText}
        onChangeText={text =>
          applySearchChange(text, showSearchArrow, setSearchText, applyFilter)
        }
        placeholder={searchPlaceholder ?? 'Search...'}
        placeholderTextColor={theme.workOrderDropdownPlaceholderText}
        autoCorrect={false}
        autoCapitalize="none"
        autoFocus={Platform.OS === 'web'}
        accessibilityLabel={searchPlaceholder ?? 'Search'}
        testID="dropdown-search-input"
      />
      {searchText !== '' && (
        <TouchableOpacity
          style={styles.closeIcon}
          accessibilityRole="button"
          accessibilityLabel="Clear search"
          onPress={() => {
            setSearchText('');
            applyFilter('');
            searchInputRef.current?.focus();
          }}>
          <LargeCloseIcon
            width={16}
            height={16}
            color={theme.dropdownTextColor}
          />
        </TouchableOpacity>
      )}
      {showSearchArrow && (
        <TouchableOpacity
          style={[styles.searchArrowBtn]}
          accessibilityRole="button"
          accessibilityLabel="Submit search"
          // @ts-ignore — tabIndex / focusable required for Tab-key focus on web
          tabIndex={0}
          focusable={true}
          onPress={() => {
            applyFilter(searchText);
            focusFirstResult();
          }}>
          <SearchArrowIcon
            color={theme.inputColor}
            bgColor={theme.searchResultBGColor}
            borderColor={theme.searchBorder}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

const Dropdown: React.FC<DropdownProps> = ({
  label,
  customTextLabelStyles,
  selectedSort,
  options,
  onSelect,
  customTextStyles,
  customContainerStyles,
  image,
  inputRef,
  customIcon,
  placeholder,
  title,
  showTickMark = false,
  showLabelWithSelected = false,
  customLabelStyles,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole = 'combobox',
  accessibilityState,
  accessible = true,
  testID,
  isDisabled = false,
  hasHeightGap = false,
  isMandatory = false,
  showSearch = false,
  searchPlaceholder,
  emptyMessage,
  numberOfLines,
  showSearchArrow = false,
  formatTextData = false,
  filterByLabelOnly = false,
}) => {
  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<TextInput>(null);
  /**
   * Ref for the first result item (or the empty message) so that after the
   * "Submit search" arrow button is pressed, accessibility focus jumps to the
   * first result instead of staying on the search input.
   */
  const firstResultRef = useRef<React.ElementRef<typeof TouchableOpacity> | null>(null);
  const emptyMessageRef = useRef<React.ElementRef<typeof View> | null>(null);
  /**
   * Filtered options stored in state so they can be updated inside a React
   * transition — keeping the TextInput update (high-priority, instant) separate
   * from the list re-render (low-priority, interruptible).
   */
  const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(options);
  /**
   * Internal ref for the trigger TouchableOpacity so we can always return
   * focus to it after the dropdown closes — regardless of whether the caller
   * also passes an `inputRef`.
   */
  const triggerRef = useRef<React.ElementRef<typeof TouchableOpacity> | null>(
    null,
  );

  /**
   * Moves accessibility focus to the first result item or empty message after
   * the search arrow is submitted.
   */
  const focusFirstResult = useCallback(
    () => scheduleFocusFirstResult(firstResultRef, emptyMessageRef),
    [],
  );

  /**
   * Returns accessibility focus to the dropdown trigger after the modal has
   * fully dismissed.
   */
  const restoreFocusToTrigger = useCallback(
    () => scheduleRestoreFocusTrigger(triggerRef),
    [],
  );

  /** Close the dropdown and reset any active search text. */
  const closeDropdown = useCallback(
    (restoreFocus = false) =>
      performCloseDropdown(
        options, setIsOpen, setSearchText, setFilteredOptions,
        setIsPositioned, restoreFocusToTrigger, restoreFocus,
      ),
    [restoreFocusToTrigger, options],
  );

  /**
   * Filters options by the given text and updates state.  React 18
   * auto-batches this with the companion setSearchText call in
   * handleSearchChange, so only one re-render fires per keystroke.
   * Delegates to module-level applyDropdownFilter to keep this callback's CC = 0.
   */
  const applyFilter = useCallback(
    (text: string) => applyDropdownFilter(text, showSearch, options, filterByLabelOnly, setFilteredOptions),
    [options, showSearch, filterByLabelOnly],
  );

  /** Index of the first item currently visible in the FlatList viewport.
   *  Used when the panel opens above the trigger. */
  const [firstVisibleIndex, setFirstVisibleIndex] = useState<number>(0);

  /** Index of the last item currently visible in the FlatList viewport.
   *  Initialised to the last data item (correct for short lists that don't
   *  scroll), then updated via onViewableItemsChanged as the user scrolls. */
  const [lastVisibleIndex, setLastVisibleIndex] = useState<number>(
    () => Math.max(0, filteredOptions.length - 1),
  );

  /** Keep lastVisibleIndex in sync when the options list changes (e.g. search). */
  useEffect(() => {
    setLastVisibleIndex(Math.max(0, filteredOptions.length - 1));
  }, [filteredOptions.length]);

  /** Stable callback — FlatList requires onViewableItemsChanged not to change
   *  reference after mount; empty deps array satisfies that contract. */
  const handleViewableItemsChanged = useCallback(
    ({viewableItems}: {viewableItems: Array<{index: number | null; isViewable: boolean}>}) => {
      updateVisibleIndices(viewableItems, setFirstVisibleIndex, setLastVisibleIndex);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Must be a stable ref — FlatList reads viewabilityConfig only once. */
  const viewabilityConfig = useRef({viewAreaCoveragePercentThreshold: 50});

  /**
   * Ref tracking the latest searchText for effects that should not re-run on
   * every keystroke (e.g. the options-sync effect below).
   */
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  /**
   * Re-apply the current filter whenever options changes (e.g. after the async
   * technician fetch completes) so the list stays consistent.
   */
  useEffect(() => {
    applyFilter(searchTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  /**
   * Compute a uniform width from the **average** character count of all option
   * labels.  Both the trigger button (selected-value box) and the dropdown
   * panel receive this same `minWidth` so their widths stay visually consistent
   * regardless of which value is currently selected.  Labels wider than this
   * value wrap naturally to additional lines inside the panel instead of being
   * truncated with an ellipsis.
   */
  const averageLabelWidth = computeAverageLabelWidth(options);

  /**
   * Maximum height of the options ScrollView, accounting for the list's
   * vertical padding and the gap between items so no item is ever clipped.
   */
  const dropdownListMaxHeight = computeDropdownListMaxHeight(filteredOptions.length);

  useEffect(
    () => setupEscapeKeyListener(isOpen, closeDropdown),
    [isOpen],
  );

  const theme = useScreenTheme('dropdown');
  const styles = useMemo(() => createDropdownStyles(theme), [theme]);
  // Build the trigger's accessible label, including the currently selected value
  // so screen readers announce e.g. "Organization, required, Active Fleet" after selection.
  const triggerBaseLabel =
    accessibilityLabel ||
    title ||
    placeholder ||
    t(accessibiltyText.dropdown.accessibilityLabelKey);

  // Always append the selected value so screen readers announce the current
  // selection regardless of whether an explicit `accessibilityLabel` was supplied.
  const triggerA11yLabel = selectedSort?.label
    ? `${triggerBaseLabel}, ${selectedSort.label}`
    : triggerBaseLabel;

  const a11yProps = useAccessibilityProps({
    accessibilityLabel: triggerA11yLabel,
    accessibilityHint:
      accessibilityHint || t(accessibiltyText.dropdown.accessibilityHintKey),
  });

  const [dropdownPos, setDropdownPos] = useState<{
    /** Absolute top when showing below the trigger; undefined when above. */
    top: number | undefined;
    /** Absolute bottom (from viewport bottom) when showing above the trigger;
     *  undefined when below.  Anchoring by bottom means the panel's bottom
     *  edge is always flush with the trigger regardless of actual panel height,
     *  so there is never a visible gap between the list and the trigger. */
    bottom: number | undefined;
    left: number | undefined;
    right: number | undefined;
    width: number;
    maxWidth?: number;
    /** Available vertical space on the chosen side.
     *  Used to cap the ScrollView when there are more than MAX_ITEMS_VISIBLE
     *  items and the panel would otherwise overflow the viewport. */
    panelMaxHeight?: number;
  }>({
    top: 0,
    bottom: undefined,
    left: undefined,
    right: undefined,
    width: 0,
  });

  /**
   * Web only — true once measureInWindow has returned valid coordinates.
   * Keeps the dropdown panel invisible (opacity 0) on its very first render
   * so it never flashes at position (0, 0) before the measurement completes.
   * Always true on native so there is zero behaviour change on mobile.
   */
  const [isPositioned, setIsPositioned] = useState(Platform.OS !== 'web');

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      onSelect(option);
      // Pass restoreFocus=true so that after selection the screen-reader cursor
      // stays on the dropdown trigger rather than jumping to an unpredictable element.
      closeDropdown(true);
    },
    [onSelect, closeDropdown],
  );

  /**
   * Measures the trigger element and computes the dropdown panel position.
   */
  const measureDropdown = useCallback(
    (onReady?: () => void) =>
      executeMeasureDropdown(
        inputRef, triggerRef, showSearch, options.length,
        {setDropdownPos, setIsPositioned, onReady},
      ),
    [inputRef, showSearch, options.length],
  );

  const handleOrientationChange = useCallback(
    () => triggerRemeasureIfOpen(isOpen, measureDropdown),
    [isOpen, measureDropdown],
  );

  /**
   * Memoised trigger-press handler — closes the dropdown when open, or delegates
   * to pressToOpenDropdown (module-level, no boolean param) when closed.
   */
  const handleTriggerPress = useCallback(
    () => {
      if (isOpen) {
        closeDropdown();
      } else {
        pressToOpenDropdown(measureDropdown, setIsOpen);
      }
    },
    [isOpen, closeDropdown, measureDropdown],
  );

  /**
   * True when the panel opens below the trigger (top is set); false when above
   * (bottom is set).  Drives the dynamic corner-radius logic so the panel and
   * trigger always share a seamless flat edge where they meet.
   */
  const showsBelow = dropdownPos.top !== undefined;

  /** Memoised FlatList renderItem — stable across renders. */
  const renderItem = useCallback(
    ({item, index}: {item: DropdownOption; index: number}) => {
      const {isLast, isFirst} = computeItemFlags(
        filteredOptions.length, lastVisibleIndex, firstVisibleIndex, showsBelow, index,
      );
      return (
        <DropdownMenuItem
          item={item}
          isSelected={selectedSort?.value === item.value}
          isLast={isLast}
          isFirst={isFirst}
          styles={styles}
          theme={theme}
          showTickMark={showTickMark}
          formatTextData={formatTextData}
          onSelect={handleSelect}
          t={t}
          showSearch={showSearch}
        />
      );
    },
    [
      styles,
      theme,
      selectedSort,
      handleSelect,
      formatTextData,
      showTickMark,
      t,
      lastVisibleIndex,
      firstVisibleIndex,
      filteredOptions.length,
      showsBelow,
    ],
  );

  /**
   * Key extractor for FlatList.  Appending the index ensures uniqueness even
   * when the options array contains items with duplicate `value` fields
   * (which would otherwise cause React reconciliation errors).
   */
  const keyExtractor = useCallback(
    (item: DropdownOption, index: number) => `${item.value}_${index}`,
    [],
  );

  return (
    <View
      style={[styles.innerContainer, isDisabled && {pointerEvents: 'none'}]}>
      {title && (
        <Text style={[styles.label, customTextLabelStyles]}>
          {title}
          {isMandatory && <Text style={styles.mandatoryMark}>{' *'}</Text>}
        </Text>
      )}

      <TouchableOpacity
        ref={node => syncTriggerRef(node, triggerRef, inputRef)}
        style={computeTriggerButtonStyle(
          isOpen, showsBelow, isHovered, isDisabled,
          averageLabelWidth, styles, customContainerStyles,
        )}
        // @ts-ignore — web only hover events
        onMouseEnter={() => setDropdownHovered(true, setIsHovered)}
        onMouseLeave={() => setDropdownHovered(false, setIsHovered)}
        onPress={isDisabled ? undefined : handleTriggerPress}
        disabled={isDisabled}
        activeOpacity={0.8}
        testID={testID}
        accessible={accessible}
        accessibilityRole={accessibilityRole}
        {...a11yProps}
        accessibilityValue={selectedSort?.label ? {text: selectedSort.label} : undefined}
        accessibilityState={{
          expanded: isOpen,
          disabled: isDisabled,
          ...accessibilityState,
        }}
        // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
        tabIndex={0}
        focusable={true}>
        {image}
        <DropdownTriggerContent
          showLabelWithSelected={showLabelWithSelected}
          label={label}
          selectedSort={selectedSort}
          placeholder={placeholder}
          numberOfLines={numberOfLines}
          customLabelStyles={customLabelStyles}
          customTextStyles={customTextStyles}
          customIcon={customIcon}
          styles={styles}
          theme={theme}
        />
      </TouchableOpacity>

      {isOpen && (
        <Modal
          transparent
          visible
          animationType="none"
          supportedOrientations={[
            'portrait',
            'landscape',
            'landscape-left',
            'landscape-right',
          ]}
          onOrientationChange={handleOrientationChange}
          accessibilityViewIsModal={true}>
          {/* Overlay — tap outside to close */}
          <TouchableWithoutFeedback onPress={() => closeDropdown()}>
            <View style={styles.modalOverlay} accessible={false} />
          </TouchableWithoutFeedback>

          {/* Dropdown panel — hidden on web until measureInWindow returns */}
          <View
            style={computePanelViewStyle(
              showSearch, showsBelow, dropdownPos, averageLabelWidth, isPositioned, styles,
            )}>
            {/* ── Search row (only when showSearch is true AND there are options) ── */}
            <DropdownSearchRow
              showSearch={showSearch}
              optionsLength={options.length}
              searchText={searchText}
              showSearchArrow={showSearchArrow}
              searchPlaceholder={searchPlaceholder}
              searchInputRef={searchInputRef}
              setSearchText={setSearchText}
              applyFilter={applyFilter}
              focusFirstResult={focusFirstResult}
              styles={styles}
              theme={theme}
            />

            {/* ── Empty message (no options or search yields nothing) ── */}
            {filteredOptions.length === 0 && emptyMessage ? (
              <View
                ref={emptyMessageRef}
                style={
                  showSearch
                    ? styles.emptyMessageSearchContainer
                    : styles.emptyMessageContainer
                }
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={emptyMessage}
                accessibilityLiveRegion="polite"
                // @ts-ignore — tabIndex / focusable required for Tab-key focus on web
                tabIndex={0}
                focusable={true}>
                <Text style={styles.emptyMessageText} accessible={false}>
                  {emptyMessage}
                </Text>
              </View>
            ) : (
              /* ── Options list — FlatList virtualises so only visible rows are
                 rendered regardless of list size (handles 200+ technicians). ── */
              <FlatList
                data={filteredOptions}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                bounces={false}
                // extraData forces FlatList to re-render when search text,
                // selection, or last-visible-index changes so the bottom-radius
                // item and active highlight are always correct.
                extraData={[searchText, lastVisibleIndex, firstVisibleIndex, showsBelow]}
                onViewableItemsChanged={handleViewableItemsChanged}
                viewabilityConfig={viewabilityConfig.current}
                initialNumToRender={MAX_ITEMS_VISIBLE + 2}
                maxToRenderPerBatch={MAX_ITEMS_VISIBLE + 2}
                windowSize={3}
                showsVerticalScrollIndicator={
                  filteredOptions.length > MAX_ITEMS_VISIBLE
                }
                style={[
                  showSearch
                    ? styles.dropdownSearchValues
                    : styles.dropdownValues,
                  {
                    maxHeight: computeListMaxHeight(
                      showSearch,
                      options.length,
                      dropdownListMaxHeight,
                      dropdownPos.panelMaxHeight,
                    ),
                  },
                ]}
                keyboardShouldPersistTaps="handled"
              />
            )}
          </View>
        </Modal>
      )}
    </View>
  );
};

export default Dropdown;
